import React, { useState, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import UserContext from "../../../context/UserContext";
import { loginUser as apiLoginUser } from "../../../api/api";
import {
  sanitizeInput,
  isValidEmail,
  loginRateLimiter,
} from "../../../utils/security";
import "./AdminLogin.css";

const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const { loginUser } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();

  // Get the intended destination or default to admin dashboard (you can create this later)
  const from = location.state?.from?.pathname || "/admin-dashboard";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validate email format
    if (!isValidEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    // Check rate limiting
    const rateLimitCheck = loginRateLimiter.check(email);
    if (!rateLimitCheck.allowed) {
      setError(rateLimitCheck.message);
      return;
    }

    setIsLoading(true);

    try {
      // Sanitize inputs before sending
      const sanitizedEmail = sanitizeInput(email.toLowerCase().trim());

      // Call backend API
      const response = await apiLoginUser(sanitizedEmail, password);

      // Check if user has admin role (data is nested in response.data)
      if (response.data?.role === "ADMIN") {
        // Login successful
        loginUser({
          userId: response.data.userId,
          email: response.data.email,
          username:
            response.data.username ||
            response.data.firstname ||
            response.data.email,
          role: response.data.role,
          token: response.data.token,
          loginTime: new Date().toISOString(),
        });

        // Reset rate limiter on successful login
        loginRateLimiter.reset(email);

        // Redirect to the intended page
        navigate(from, { replace: true });
      } else {
        setError("Access denied. Admin privileges required.");
        setIsLoading(false);
      }
    } catch (err) {
      // Generic error message to avoid information leakage
      setError("Invalid credentials. Please try again.");
      setIsLoading(false);
    }
  };

  return (
    <div className="admin-login-container">
      <div className="admin-login-box">
        <div className="admin-login-header">
          <h1>
            👑 Admin Portal
            <span className="admin-badge">ADMIN</span>
          </h1>
          <p>Restricted access - Administrators only</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form className="admin-login-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="admin-email">Admin Email</label>
            <input
              id="admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter admin email"
              required
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="admin-password">Admin Password</label>
            <input
              id="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              required
            />
          </div>

          <button
            type="submit"
            className="admin-login-button"
            disabled={isLoading}
          >
            {isLoading ? "Authenticating..." : "Admin Login"}
          </button>
        </form>

        <div className="back-link">
          <a href="/">← Back to Home</a>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
